import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function EmailSent({ email, username, onVerify }) {
  const [success, setSuccess] = useState('');
  const [validated, setValidated] = useState(0);
  const navigate = useNavigate();
  const EMAIL_SENT_KEY = `email_sent_${username}`; // scoped per-user

  const checkValidation = async () => {
    try {
      const res = await axios.get('/api/emailverify', { params: { username } });
      if (res.data.uservalidation) {
        setSuccess('User email already validated');
        setValidated(true);
      } else if (res.data.success && res.data.valid) {
        setSuccess('A verification email was already sent. Please check your inbox.');
      } else if (!localStorage.getItem(EMAIL_SENT_KEY)) {
        try {
          await axios.post('/api/emailverify', { email, username });
        } catch (err) {
          await axios.delete('/api/login', { params: { username } });
          throw err;
        }

        setSuccess(
          'Verification email sent. Once verified the page will refresh to your To-Do page!'
        );
        localStorage.setItem(EMAIL_SENT_KEY, 'true'); // Mark as sent
      }
    } catch (err) {
      console.error('Error checking email verification:', err);
      throw err;
    }
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const badverify = async () => {
    await new Promise((resolve) => setTimeout(resolve, 3000)); // 3 second delay
    onVerify(null);
  };

  useEffect(() => {
    const runCheck = async () => {
      try {
        await checkValidation();
        const interval = setInterval(() => {
          checkValidation().catch((err) => {
            console.error('Polling error:', err);
            showError(err.response?.data?.message || 'Unknown error');
            badverify();
          });
        }, 5000);

        return () => clearInterval(interval);
      } catch (err) {
        console.error('Initial email verification error:', err);
        setSuccess(err.response?.data?.message || 'Unknown error');
        await delay(10000);
        badverify();
      }
    };

    runCheck();
  }, []);

  useEffect(() => {
    if (validated) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr); // convert to object
        user.data.verified = 1; // update value
        onVerify(user);
      }
      localStorage.removeItem(EMAIL_SENT_KEY);
      setTimeout(() => {
        navigate('/login'); // Redirect to login
      }, 3000); // Give time for user to see the message
    }
  }, [validated, navigate]);

  return (
    <div className="auth-page">
      <div className="auth-layout">
        <aside className="auth-hero">
          <div>
            <p className="auth-hero__eyebrow">Verification</p>
            <h1 className="auth-hero__title">Check your inbox</h1>
            <p className="auth-hero__copy">
              We sent a verification link and are waiting for confirmation.
            </p>
          </div>

          <div className="auth-hero__notes">
            <span className="auth-hero__note">One-time link</span>
            <span className="auth-hero__note">Auto refresh</span>
            <span className="auth-hero__note">Secure signup</span>
          </div>
        </aside>

        <section className="auth-card" style={{ animation: 'slideUp 0.6s ease-out' }}>
          <h2 className="auth-card__title">Verification sent</h2>
          <p className="auth-card__copy">
            {success || 'Hold tight while we wait for the email to be confirmed.'}
          </p>

          <div className="auth-status is-success">
            {success || 'Your verification screen is active.'}
          </div>
        </section>
      </div>
    </div>
  );
}
